import { useEffect, useState } from "react";
import { supabase } from "../utils/supabase";

/**
 * SupabaseTest - A component to verify Supabase connection
 * Shows connection status and task data from the new database
 */
function SupabaseTest() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState("Checking...");
  const [dbInfo, setDbInfo] = useState(null);

  useEffect(() => {
    async function checkConnection() {
      try {
        console.log("Testing Supabase connection...");

        // Get database connection info
        const dbUrl = supabase.supabaseUrl;
        setDbInfo({
          url: dbUrl,
          isConfigured: !!dbUrl && dbUrl.includes("supabase.co"),
        });

        // Test the connection by fetching tasks
        const { data, error } = await supabase.from("tasks").select("*");

        if (error) {
          console.error("Supabase connection error:", error);
          setError(error.message);
          setConnectionStatus("Failed to connect");
        } else {
          console.log(
            "Supabase connected successfully, tasks:",
            data?.length || 0
          );
          setTasks(data || []);
          setConnectionStatus("Connected successfully");
        }
      } catch (err) {
        console.error("Unexpected error:", err);
        setError(err.message);
        setConnectionStatus("Error connecting");
      } finally {
        setLoading(false);
      }
    }

    checkConnection();
  }, []);

  return (
    <div className="p-4 bg-gray-800 rounded-lg shadow-lg">
      <h2 className="text-xl font-bold mb-4 text-white">
        Supabase Connection Test
      </h2>

      <div className="mb-4">
        <p className="text-white">
          Connection Status:
          <span
            className={`ml-2 px-2 py-1 rounded text-sm ${
              connectionStatus === "Connected successfully"
                ? "bg-green-500"
                : connectionStatus === "Checking..."
                  ? "bg-yellow-500"
                  : "bg-red-500"
            }`}
          >
            {connectionStatus}
          </span>
        </p>

        {dbInfo && (
          <div className="mt-2 text-sm text-gray-300">
            <p>Database URL: {dbInfo.url}</p>
            <p>Configuration: {dbInfo.isConfigured ? "Valid" : "Invalid"}</p>
          </div>
        )}

        {error && (
          <div className="mt-2 p-2 bg-red-900/30 border border-red-700 rounded">
            <p className="text-red-400">Error: {error}</p>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2 text-white">
          Tasks in Database:
        </h3>
        {loading ? (
          <p className="text-gray-400">Loading tasks...</p>
        ) : tasks.length > 0 ? (
          <ul className="space-y-2">
            {tasks.map((task) => (
              <li key={task.id} className="p-2 bg-gray-700 rounded">
                <p className="font-medium text-white">{task.title}</p>
                <p className="text-sm text-gray-300">{task.description}</p>
                <span className="inline-block mt-1 px-2 py-1 text-xs rounded bg-blue-600 text-white">
                  {task.status}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-400">No tasks found in the database.</p>
        )}
      </div>
    </div>
  );
}

export default SupabaseTest;
